import {
  AlertCircle,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  FileType,
  Image as ImageIcon,
  Loader2,
  PenLine,
  RotateCw,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  FILE_STATUS_LABELS,
  KNOWLEDGE_SOURCE_LABELS,
  ORGANISE_GAP_LABELS,
  type BrainItem,
  type OrganiseGap,
  documentDisplayName,
} from "@gb/contracts";
import { AuthedImage } from "@/components/gb/authed-file";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { formatSize } from "@/features/project/document-row";
import type { UploadState } from "@/features/project/documents-api";
import { cn } from "@/lib/utils";
import { photoThumbnailPath } from "@/features/journal/journal-photos-api";
import { roleSentence } from "./family-words";
import { SOURCE_ICONS, SOURCE_STYLES } from "./source-display";
import { removedAtSourceLabel } from "./removed-at-source";

/**
 * A file type's icon, from its MIME type.
 *
 * A generic page icon for everything would be honest and useless: the point of
 * a file browser is that a spreadsheet, a drawing and a contract are
 * distinguishable at a glance, before any text is read.
 */
function iconFor(mimeType: string): LucideIcon {
  const type = mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (type.startsWith("image/")) return ImageIcon;
  if (type === "application/pdf") return FileType;
  if (type.includes("spreadsheet") || type.includes("ms-excel")) return FileSpreadsheet;
  return FileText;
}

/** `"application/pdf"` → `"PDF"`. What the row shows next to the size. */
function formatKind(mimeType: string, filename: string): string {
  const extension = filename.slice(filename.lastIndexOf(".") + 1);
  if (extension && extension.length <= 4 && extension !== filename) {
    return extension.toUpperCase();
  }
  const type = mimeType.split("/")[1] ?? mimeType;
  return type.slice(0, 8).toUpperCase();
}

/** One file or one entry. Both open something, so both are a whole row button. */
export function BrainItemRow({
  item,
  projectId,
  onOpen,
  shelfCategory,
}: {
  item: BrainItem;
  projectId: string;
  onOpen: () => void;
  /** The key of the category shelf it is listed on, if any (see `DocumentBody`). */
  shelfCategory?: string;
}) {
  return (
    <li className="border-b border-border-inner last:border-b-0">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-subtle"
      >
        {item.kind === "document" ? (
          <DocumentBody item={item} shelfCategory={shelfCategory} />
        ) : (
          <EntryBody item={item} projectId={projectId} />
        )}
        <ChevronRight size={18} className="shrink-0 text-muted" aria-hidden />
      </button>
    </li>
  );
}

/**
 * What a file's row says: its name, its markers, whether it is still read,
 * and the category it lives in.
 *
 * The category is said on every list but its own shelf (`document-ai/0032`):
 * there every row lives in it. On that shelf's "Also relevant here" group it
 * is the line that says where the file is.
 */
export function DocumentBody({
  item,
  shelfCategory,
}: {
  item: Extract<BrainItem, { kind: "document" }>;
  /** The key of the category shelf it is listed on, if any. */
  shelfCategory?: string;
}) {
  const Icon = iconFor(item.mimeType);
  // `queued` and `running` are both "we are still reading this". The
  // distinction matters to the sweeper, not to the person waiting.
  const reading =
    item.processingStatus === "queued" || item.processingStatus === "running";

  return (
    <>
      <span className="flex shrink-0 text-muted">
        <Icon size={20} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          {/* The card's title once it is written; until then, the filename is
              the only name the file has. */}
          <span className="truncate font-semibold text-ink">
            {item.title ?? documentDisplayName(item.filename)}
          </span>
          {/* What kind of document the card says it is, in the file's own
              terms. No pill until the card is written: a guessed label would
              be the one thing on the row nobody read. */}
          {item.docType && (
            <span
              className={cn(
                "shrink-0 rounded-full border px-2 py-[2px] font-display text-[0.62rem] font-semibold tracking-[0.06em] uppercase",
                SOURCE_STYLES["project-doc"],
              )}
            >
              {item.docType}
            </span>
          )}
        </div>

        {/* The search hit, when there was one — this is the line that makes a
            75-page manual a useful result rather than just a filename. */}
        {item.snippet ? (
          <div className="mt-0.5 truncate text-[0.82rem] text-ink-slate">{item.snippet}</div>
        ) : null}

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[0.78rem] text-muted">
          {/* First, because they change what the file is worth: a superseded
              file is not the one answers use, and a question waits for a
              person. Text only — the row is one button, so no control goes
              in it (`document-ai/0016`).

              The status leads all of them. A file the agent may not read is a
              bigger fact than which version it is (`document-ai/0013`), and
              only the `status` filter lists one at all — so the marker is also
              what says why this row is here. Keep and Remove are on the file's
              own screen, for the same reason there is no control in the row. */}
          {/* Deleted in SharePoint or Outlook, or moved out of the project's
              folder (`plans/sharepoint-sync.md` question 15,
              `plans/outlook-sync.md` question 22): kept, and out of answers,
              whatever its status. */}
          {item.removedAtSource && (
            <>
              <span className="font-semibold text-warn-text">{removedAtSourceLabel(item.source)}</span>
              <span aria-hidden>·</span>
              {!item.status.hidden && (
                <>
                  <span className="font-semibold text-ink-slate">Not used in answers</span>
                  <span aria-hidden>·</span>
                </>
              )}
            </>
          )}
          {item.status.hidden && item.status.status && (
            <>
              <span className="font-semibold text-warn-text">
                {FILE_STATUS_LABELS[item.status.status]}
              </span>
              <span aria-hidden>·</span>
              <span className="font-semibold text-ink-slate">Not used in answers</span>
              <span aria-hidden>·</span>
            </>
          )}
          {item.question && (
            <>
              <span className="font-semibold text-warn-text">Needs a decision</span>
              <span aria-hidden>·</span>
            </>
          )}
          {/* The check would have hidden it, and asked a person instead
              (2026-09-26). It is still used in answers until someone decides,
              on the file's own screen. */}
          {item.status.question && (
            <>
              <span className="font-semibold text-warn-text">Hide or keep?</span>
              <span aria-hidden>·</span>
            </>
          )}
          {item.role !== "current" && (
            <>
              <span className="font-semibold text-ink-slate">
                {roleSentence(item.role, item.relatedName)}
              </span>
              <span aria-hidden>·</span>
            </>
          )}
          <span>{formatKind(item.mimeType, item.filename)}</span>
          <span aria-hidden>·</span>
          <span>{formatSize(item.sizeBytes)}</span>
          {item.pageCount !== null && (
            <>
              <span aria-hidden>·</span>
              <span>
                {item.pageCount} {item.pageCount === 1 ? "page" : "pages"}
              </span>
            </>
          )}
          {item.citation && (
            <>
              <span aria-hidden>·</span>
              <span className="font-semibold text-brand">{item.citation}</span>
            </>
          )}
          <Added iso={item.createdAt} separator />
          {/* What of it nothing has read, and what no step has organised
              (2026-09-26, RET-12). Not while it is being read: the read
              about to finish decides both again. */}
          {!reading && item.picturesNotRead !== null && item.picturesNotRead > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="font-semibold text-ink-slate">{picturesNotReadWords(item.picturesNotRead)}</span>
            </>
          )}
          {item.organiseGaps.length > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>{organiseGapWords(item.organiseGaps)}</span>
            </>
          )}
          {/* Quietly, last: where the file lives, in the tenant's words. */}
          {item.category && item.category.key !== shelfCategory && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">In {item.category.name}</span>
            </>
          )}
        </div>
      </div>

      {reading ? (
        // A document is listed the moment its bytes land, never gated on the
        // parse — so this is what stops a freshly uploaded file looking either
        // broken or finished while it is neither.
        <span className="flex shrink-0 items-center gap-1.5 text-[0.78rem] font-semibold text-muted">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          Reading…
        </span>
      ) : item.processingStatus === "failed" ? (
        // The file is stored, downloadable and in the Brain regardless — only
        // the reading failed, and saying so is better than a silent gap where
        // page citations should be.
        <span
          className="flex shrink-0 items-center gap-1.5 text-[0.78rem] font-semibold text-warn"
          title="The file is stored and can be opened — but it couldn’t be read into pages, so it has no citations."
        >
          <AlertCircle size={14} aria-hidden />
          Not read
        </span>
      ) : item.readFailure ? (
        // Every try of a step that failed has run out (2026-09-26): part of
        // the file, or all of it, will not be read unless someone acts. The
        // sentence says which part.
        <span
          className="flex shrink-0 items-center gap-1.5 text-[0.78rem] font-semibold text-warn"
          title={`${item.readFailure} The file is stored and can be opened.`}
        >
          <AlertCircle size={14} aria-hidden />
          Not read in full
        </span>
      ) : null}
    </>
  );
}

/** "1 picture not read", "12 pictures not read" (2026-09-26, RET-12). */
export function picturesNotReadWords(count: number): string {
  return `${count} ${count === 1 ? "picture" : "pictures"} not read`;
}

/** "Not organised yet: no links yet", with each step that has not run (RET-12). */
export function organiseGapWords(gaps: readonly OrganiseGap[]): string {
  return `Not organised yet: ${gaps.map((gap) => ORGANISE_GAP_LABELS[gap]).join(", ")}`;
}

function EntryBody({
  item,
  projectId,
}: {
  item: Extract<BrainItem, { kind: "entry" }>;
  projectId: string;
}) {
  const Icon = SOURCE_ICONS[item.sourceType];

  return (
    <>
      {item.photo ? (
        // The picture itself, not an icon for it. A grid of camera glyphs tells
        // you nothing; a thumbnail is the whole reason to browse photographs.
        // `AuthedImage`, not `<img>`: the byte route is behind the guard and an
        // `<img src>` sends no bearer token.
        <AuthedImage
          path={photoThumbnailPath(projectId, item.photo.entryId, item.photo.id)}
          alt=""
          className="size-10 shrink-0 rounded-control border border-border object-cover"
        />
      ) : (
        <span className="flex shrink-0 text-muted">
          <Icon size={20} aria-hidden />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="truncate font-semibold text-ink">{item.title}</span>
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-[2px] font-display text-[0.62rem] font-semibold tracking-[0.06em] uppercase",
              SOURCE_STYLES[item.sourceType],
            )}
          >
            {KNOWLEDGE_SOURCE_LABELS[item.sourceType]}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[0.82rem] text-ink-slate">{item.snippet}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[0.78rem] text-muted">
          {item.citation && <span className="truncate">{item.citation}</span>}
          <Added iso={item.createdAt} separator={Boolean(item.citation)} />
        </div>
      </div>
    </>
  );
}

/**
 * When this arrived in the Brain.
 *
 * `createdAt` is when the *row* was written here, not when the source document
 * was authored — a contract signed in March and imported today reads "Added
 * today", which is the honest claim. The Brain knows when it learned something;
 * it does not generally know when the thing happened.
 *
 * Relative for the first week, then a date. "3 days ago" is what you want while
 * you still remember doing it; after that the date is what you are scanning for.
 */
function Added({ iso, separator }: { iso: string; separator: boolean }) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;

  const days = Math.floor((Date.now() - when.getTime()) / 86_400_000);
  const label =
    days <= 0 ? "Added today" : days === 1 ? "Added yesterday" : days < 7 ? `Added ${days} days ago` : `Added ${formatDate(iso.slice(0, 10))}`;

  return (
    <>
      {/* The separator belongs to whatever precedes this, not to the date — an
          entry with no citation has nothing in front of it, and a leading "·"
          reads as a missing field rather than as punctuation. */}
      {separator && <span aria-hidden>·</span>}
      <time dateTime={iso}>{label}</time>
    </>
  );
}

/**
 * A file still on its way up.
 *
 * Its own row rather than a banner, so it sits exactly where the finished file
 * will be — and a failure leaves something retryable in place rather than
 * making the file vanish, which is the difference between "that didn't work"
 * and the user believing a document is on file when it never arrived.
 */
export function BrainUploadRow({
  upload,
  onRetry,
  onDismiss,
}: {
  upload: UploadState;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const failed = upload.status === "error";

  return (
    <li className="flex items-center gap-3 border-b border-border-inner px-4 py-3 last:border-b-0">
      <span className={cn("flex shrink-0", failed ? "text-danger" : "text-muted")}>
        {failed ? (
          <AlertCircle size={20} aria-hidden />
        ) : upload.status === "processing" ? (
          <Loader2 size={20} className="animate-spin" aria-hidden />
        ) : (
          <PenLine size={20} aria-hidden />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-ink">{upload.filename}</div>
        {failed ? (
          <div className="mt-0.5 text-[0.8rem] font-semibold text-danger">{upload.error}</div>
        ) : (
          <>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-subtle">
              <div
                className="h-full rounded-full bg-brand transition-[width]"
                style={{ width: `${upload.progress}%` }}
              />
            </div>
            <div className="mt-1 text-[0.78rem] text-muted">
              {upload.status === "queued"
                ? "Waiting…"
                : upload.status === "processing"
                  ? "Filing it in the Brain…"
                  : `${upload.progress}% of ${formatSize(upload.sizeBytes)}`}
            </div>
          </>
        )}
      </div>

      {/* Announced politely so progress is perceivable without sight of the bar,
          but not so often that it floods the screen reader. */}
      <span aria-live="polite" className="sr-only">
        {upload.status === "done"
          ? `${upload.filename} uploaded`
          : failed
            ? `${upload.filename} failed: ${upload.error}`
            : ""}
      </span>

      {failed && (
        <span className="flex shrink-0 items-center gap-1.5">
          {/* A refused file would be refused again (`architecture/0031`). */}
          {!upload.refused && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              <RotateCw size={14} />
              Retry
            </Button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            aria-label={`Discard ${upload.filename}`}
            className="flex size-8 items-center justify-center rounded-control text-muted hover:bg-subtle hover:text-danger"
          >
            <X size={17} />
          </button>
        </span>
      )}
    </li>
  );
}
